import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@readsavor/api-client';
import { useAuthStore, userQueryKeys } from '@readsavor/auth';
/**
 * Provides a mutation for initializing a new user.
 *
 * This hook triggers the backend initialization process. Due to the backend
 * using admin privileges which suppress real-time events, this hook now
 * includes an `onSuccess` callback to manually invalidate relevant caches,
 * ensuring the UI reflects the user's initialized state without a page refresh.
 *
 * UI components can still pass their own `onSuccess`, `onError` callbacks to
 * handle side effects like logging or analytics, which will be called after
 * the cache invalidation logic.
 *
 * @example
 * const { mutate: initializeUser, isLoading } = useUserInitialization({
 *   onSuccess: () => console.log('User initialization succeeded and caches are invalidated.'),
 *   onError: (error) => console.error('Initialization trigger failed:', error),
 * });
 *
 * // Typically called once after a user signs in for the first time
 * initializeUser();
 */
export const useUserInitialization = (options) => {
    const queryClient = useAuthStore(state => state.queryClient);
    return useMutation({
        mutationFn: () => apiClient.integration.initializeUser(),
        onSuccess: (data, variables, context) => {
            // Invalidate all queries associated with the current user.
            // User initialization is a significant event that affects multiple domains
            // (subscription, credits, coupons). Invalidating the user's top-level
            // query key ensures all related data is refetched consistently.
            // This is the robust solution to the backend's suppression of real-time events.
            // Step 1: Invalidate queries to mark them as stale.
            // This is for queries that are already in a success state.
            if (queryClient) {
                // Step 1: Invalidate queries to mark them as stale.
                queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
                // Step 2: Force refetch all user-related queries.
                queryClient.refetchQueries({ queryKey: userQueryKeys.all });
            }
            else {
                console.warn('[useUserInitialization] QueryClient not found in auth store. Skipping automatic refetch.');
            }
            // Ensure any `onSuccess` callback from the UI component is also called.
            if (options?.onSuccess) {
                options.onSuccess(data, variables, context);
            }
        },
        // Spread the rest of the options to allow UI components to pass their own callbacks
        ...options,
    });
};
//# sourceMappingURL=useUserInitialization.js.map